import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    IconButton,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/Key';
import { useTheme } from '../../ThemeContext';
import { WebhookEntry, WebhookListResponse } from '../../interface/webhooks';
import JsonViewer from '../atoms/JsonViewer';
import { get, del } from '../../redux/http/request';
import execute from '../../redux/http/execute';

const COMMON_SOURCES = ['encompass', 'validator', 'halcyondirect'];

type ViewMode = 'list' | 'detail';

interface DecodedJwt {
    header: unknown;
    payload: unknown;
    signature: string;
}

const decodeJwt = (jwt: string): DecodedJwt | null => {
    try {
        const parts = jwt.split('.');
        if (parts.length !== 3) return null;

        // Base64url decode
        const base64UrlDecode = (str: string): string => {
            // Replace base64url characters with base64 characters
            let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
            // Pad with = if needed
            const padding = base64.length % 4;
            if (padding) {
                base64 += '='.repeat(4 - padding);
            }
            return atob(base64);
        };

        const header = JSON.parse(base64UrlDecode(parts[0]));
        const payload = JSON.parse(base64UrlDecode(parts[1]));

        return {
            header,
            payload,
            signature: parts[2],
        };
    } catch {
        return null;
    }
};

const WebhookDebugger = () => {
    const navigate = useNavigate();
    const { mode, toggleColorMode } = useTheme();

    // View state
    const [view, setView] = useState<ViewMode>('list');

    // List state
    const [source, setSource] = useState<string>('encompass');
    const [customSource, setCustomSource] = useState<string>('');
    const [webhooks, setWebhooks] = useState<WebhookEntry[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [limit, setLimit] = useState<number>(50);

    // Detail state
    const [selectedWebhook, setSelectedWebhook] = useState<WebhookEntry | null>(null);

    // Dialog states
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [jwtModalOpen, setJwtModalOpen] = useState<boolean>(false);
    const [selectedJwt, setSelectedJwt] = useState<DecodedJwt | null>(null);

    // UI feedback
    const [copySuccess, setCopySuccess] = useState<boolean>(false);

    const activeSource = source === 'custom' ? customSource : source;

    const fetchWebhooks = useCallback(async () => {
        if (!activeSource) {
            setError('Please enter a source');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await execute(get, `/weblogs/${activeSource}`, { limit });
            const data = response.data as WebhookListResponse;
            setWebhooks(data.webhooks || []);
            setTotal(data.total || 0);
        } catch (err: any) {
            setError(err?.response?.data?.error || err?.message || 'Failed to fetch webhooks');
            setWebhooks([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [activeSource, limit]);

    const clearWebhooks = async () => {
        if (!activeSource) return;

        setLoading(true);
        try {
            await execute(del, `/weblogs/${activeSource}`, {});
            setWebhooks([]);
            setTotal(0);
            setDeleteDialogOpen(false);
        } catch (err: any) {
            setError(err?.response?.data?.error || err?.message || 'Failed to clear webhooks');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (webhook: WebhookEntry) => {
        setSelectedWebhook(webhook);
        setView('detail');
    };

    const handleBackToList = () => {
        setView('list');
    };

    const handleCopyPayload = async (payload: unknown) => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch {
            setError('Failed to copy to clipboard');
        }
    };

    const handleJwtClick = (jwt: string) => {
        const decoded = decodeJwt(jwt);
        if (decoded) {
            setSelectedJwt(decoded);
            setJwtModalOpen(true);
        } else {
            setError('Failed to decode JWT');
        }
    };

    const formatTimestamp = (timestamp: number) => {
        const date = new Date(timestamp);
        return date.toLocaleString();
    };

    const getPayloadPreview = (payload: unknown): string => {
        const str = JSON.stringify(payload);
        return str.length > 100 ? `${str.substring(0, 100)}...` : str;
    };

    useEffect(() => {
        if (activeSource) {
            fetchWebhooks();
        }
    }, []);

    // Render list view
    const renderListView = () => (
        <>
            {/* Controls Section */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Source</InputLabel>
                        <Select
                            value={source}
                            label="Source"
                            onChange={(e) => setSource(e.target.value)}
                        >
                            {COMMON_SOURCES.map((s) => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                            <MenuItem value="custom">Custom...</MenuItem>
                        </Select>
                    </FormControl>

                    {source === 'custom' && (
                        <TextField
                            label="Custom Source"
                            value={customSource}
                            onChange={(e) => setCustomSource(e.target.value)}
                            sx={{ minWidth: 200 }}
                        />
                    )}

                    <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Limit</InputLabel>
                        <Select
                            value={limit}
                            label="Limit"
                            onChange={(e) => setLimit(e.target.value as number)}
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                            <MenuItem value={200}>200</MenuItem>
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={fetchWebhooks}
                        disabled={loading || !activeSource}
                    >
                        Refresh
                    </Button>

                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteDialogOpen(true)}
                        disabled={loading || !activeSource || webhooks.length === 0}
                    >
                        Clear All
                    </Button>
                </Box>
            </Paper>

            {/* Results Section */}
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Webhooks
                        {total > 0 && (
                            <Chip
                                label={`${total} total`}
                                size="small"
                                sx={{ ml: 2 }}
                            />
                        )}
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : webhooks.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                            No webhooks found for source &quot;{activeSource}&quot;
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Received At</TableCell>
                                    <TableCell>Payload Preview</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {webhooks.map((webhook) => (
                                    <TableRow
                                        key={webhook.id}
                                        hover
                                        onClick={() => handleViewDetails(webhook)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                {webhook.id.substring(0, 8)}...
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {formatTimestamp(webhook.timestamp)}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 400 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.75rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {getPayloadPreview(webhook.payload)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Copy Payload">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCopyPayload(webhook.payload);
                                                    }}
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </>
    );

    // Render detail view
    const renderDetailView = () => {
        if (!selectedWebhook) return null;

        return (
            <Paper sx={{ p: 3 }}>
                {/* Detail Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconButton onClick={handleBackToList} color="primary" sx={{ mr: 2 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h6">Webhook Details</Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            ID: {selectedWebhook.id}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* General Info */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>General</Typography>
                        <Box sx={{ pl: 2 }}>
                            <Typography variant="body2"><strong>Source:</strong> {selectedWebhook.source}</Typography>
                            <Typography variant="body2"><strong>Method:</strong> {selectedWebhook.method}</Typography>
                            <Typography variant="body2"><strong>Received:</strong> {formatTimestamp(selectedWebhook.timestamp)}</Typography>
                        </Box>
                    </Box>

                    {/* Payload */}
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">Payload</Typography>
                            <Button
                                size="small"
                                startIcon={<ContentCopyIcon />}
                                onClick={() => handleCopyPayload(selectedWebhook.payload)}
                            >
                                Copy
                            </Button>
                        </Box>
                        <JsonViewer
                            data={selectedWebhook.payload}
                            maxHeight={400}
                            onJwtClick={handleJwtClick}
                        />
                    </Box>

                    {/* Headers */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Headers</Typography>
                        <JsonViewer
                            data={selectedWebhook.headers}
                            maxHeight={250}
                            onJwtClick={handleJwtClick}
                        />
                    </Box>

                    {/* Query Parameters */}
                    {Object.keys(selectedWebhook.query || {}).length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Query Parameters</Typography>
                            <JsonViewer
                                data={selectedWebhook.query}
                                maxHeight={150}
                                onJwtClick={handleJwtClick}
                            />
                        </Box>
                    )}
                </Box>
            </Paper>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4, minHeight: '100vh', position: 'relative' }}>
            {/* Theme Toggle Button */}
            <IconButton
                sx={{ position: 'absolute', top: 16, right: 16 }}
                onClick={toggleColorMode}
                color="inherit"
            >
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>

            {/* Back Button (only in list view) */}
            {view === 'list' && (
                <Box sx={{ mb: 4 }}>
                    <IconButton onClick={() => navigate('/')} color="primary">
                        <ArrowBackIcon />
                    </IconButton>
                </Box>
            )}

            {/* Logo Section */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <Box
                    component="img"
                    sx={{
                        height: 80,
                        maxWidth: 300,
                    }}
                    alt="Halcyon Still Water"
                    src={mode === 'dark' ? '/logo-white.png' : '/logo.png'}
                />
            </Box>

            {/* Title Section */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                    Webhook Debugger
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    View and manage captured webhooks for debugging
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Main Content */}
            {view === 'list' ? renderListView() : renderDetailView()}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Clear All Webhooks?</DialogTitle>
                <DialogContent>
                    <Typography>
                        This will permanently delete all {total} webhooks for source &quot;{activeSource}&quot;.
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={clearWebhooks} color="error" variant="contained">
                        Delete All
                    </Button>
                </DialogActions>
            </Dialog>

            {/* JWT Decode Modal */}
            <Dialog
                open={jwtModalOpen}
                onClose={() => setJwtModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <KeyIcon />
                        <span>JWT Decoded</span>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedJwt && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Header</Typography>
                                <JsonViewer data={selectedJwt.header} maxHeight={150} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Payload</Typography>
                                <JsonViewer data={selectedJwt.payload} maxHeight={300} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Signature</Typography>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        backgroundColor: mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {selectedJwt.signature}
                                </Paper>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setJwtModalOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Copy Success Snackbar */}
            {copySuccess && (
                <Alert
                    severity="success"
                    sx={{
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        zIndex: 9999,
                    }}
                >
                    Copied to clipboard!
                </Alert>
            )}
        </Container>
    );
};

export default WebhookDebugger;
